// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import startOfMonth from "date-fns/startOfMonth";

import type { UsTxArsErsSharedFormInformation } from "~datatypes";

import { toTitleCase } from "../../../utils";
import type { Client } from "../../Client";
import type { Opportunity } from "../types";

/**
 * Draft data fields that are identical across the ARS and ERS forms (header
 * info plus the 5 signature blocks). Form-specific draft data types should
 * intersect with this.
 */
export type UsTxArsErsSharedDraftData = {
  clientName: string;
  tdcjNumberAndSid: string;
  eligibilityMonthString: string;
  restitutionObligationsCheck: boolean;
  warrantCheck: boolean;
  societyBestInterestCheck: boolean;
  officerName: string;
  supervisingOfficerDate: string;
  supervisingOfficerRecommendCheckYes: boolean;
  supervisingOfficerRecommendCheckNo: boolean;
  supervisingOfficerSignature: string;
  supervisingOfficerRemarks: string;
  unitSupervisorName: string;
  unitSupervisorConcurWithSupervisingOfficerCheckYes: boolean;
  unitSupervisorConcurWithSupervisingOfficerCheckNo: boolean;
  unitSupervisorDate: string;
  unitSupervisorSignature: string;
  unitSupervisorRemarks: string;
  paroleSupervisorName: string;
  paroleSupervisorDate: string;
  paroleSupervisorConcurWithSupervisingOfficerCheckYes: boolean;
  paroleSupervisorConcurWithSupervisingOfficerCheckNo: boolean;
  paroleSupervisorSignature: string;
  paroleSupervisorRemarks: string;
  assistantRegionDirectorName: string;
  assistantRegionDirectorDate: string;
  assistantRegionDirectorConcurWithSupervisingOfficerCheckYes: boolean;
  assistantRegionDirectorConcurWithSupervisingOfficerCheckNo: boolean;
  assistantRegionDirectorSignature: string;
  assistantRegionDirectorRemarks: string;
  regionDirectorName: string;
  regionDirectorDate: string;
  regionDirectorConcurWithSupervisingOfficerCheckYes: boolean;
  regionDirectorConcurWithSupervisingOfficerCheckNo: boolean;
  regionDirectorSignature: string;
  regionDirectorRemarks: string;
};

// A map of what fields are required to be able to submit the opportunity to the next reviewer for each role.
// A string entry must be present in draftData
export const US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS = {
  paroleOfficer: [
    "officerName",
    "supervisingOfficerSignature",
    "supervisingOfficerDate",
    "supervisingOfficerRecommendCheckYes",
  ],
  unitSupervisor: [
    "unitSupervisorSignature",
    "unitSupervisorName",
    "unitSupervisorDate",
    "unitSupervisorConcurWithSupervisingOfficerCheckYes",
  ],
  paroleSupervisor: [
    "paroleSupervisorSignature",
    "paroleSupervisorName",
    "paroleSupervisorDate",
    "paroleSupervisorConcurWithSupervisingOfficerCheckYes",
  ],
  assistantRegionDirector: [
    "assistantRegionDirectorSignature",
    "assistantRegionDirectorName",
    "assistantRegionDirectorDate",
    "assistantRegionDirectorConcurWithSupervisingOfficerCheckYes",
  ],
  regionDirector: [
    "regionDirectorSignature",
    "regionDirectorName",
    "regionDirectorDate",
    "regionDirectorConcurWithSupervisingOfficerCheckYes",
  ],
} satisfies Record<string, string[]>;

export type UsTxArsErsBlockingSubmitFieldKey =
  keyof typeof US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS;

// The remarks field for each role in the review chain, keyed the same as
// US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS. Remarks are never required to submit, but
// while a step is in "revisions requested" status, that step's own blocking fields are
// often already complete from a prior submission — so editing only the remarks field is
// accepted as evidence that the requested revision was addressed.
export const US_TX_ARS_ERS_REMARKS_FIELDS: Record<string, string> = {
  paroleOfficer: "supervisingOfficerRemarks",
  unitSupervisor: "unitSupervisorRemarks",
  paroleSupervisor: "paroleSupervisorRemarks",
  assistantRegionDirector: "assistantRegionDirectorRemarks",
  regionDirector: "regionDirectorRemarks",
};

export function prefilledArsErsSharedDraftData(
  client: Client,
  formInformation: UsTxArsErsSharedFormInformation,
  includeAutopopulatedNames = false,
): Partial<UsTxArsErsSharedDraftData> {
  const {
    tdcjNumber,
    unitSupervisor,
    paroleSupervisor,
    assistantRegionDirector,
    regionDirector,
  } = formInformation;

  const tdcjNumberAndSid = tdcjNumber
    ? `${tdcjNumber} / ${client.displayId}`
    : client.displayId;

  const eligibilityMonthString = startOfMonth(new Date()).toLocaleString(
    "en-US",
    {
      month: "long",
      year: "numeric",
    },
  );

  return {
    clientName: client.displayName,
    tdcjNumberAndSid,
    eligibilityMonthString,
    restitutionObligationsCheck: true,
    warrantCheck: true,
    societyBestInterestCheck: true,
    officerName: client.assignedStaffFullName,
    ...(includeAutopopulatedNames && {
      unitSupervisorName: unitSupervisor ? toTitleCase(unitSupervisor) : "",
      paroleSupervisorName: paroleSupervisor
        ? toTitleCase(paroleSupervisor)
        : "",
      assistantRegionDirectorName: assistantRegionDirector
        ? toTitleCase(assistantRegionDirector)
        : "",
      regionDirectorName: regionDirector ? toTitleCase(regionDirector) : "",
    }),
  };
}

/**
 * Compares a draft value against a field's prepopulated value, ignoring
 * leading/trailing whitespace and casing for strings (e.g. "jane doe " should
 * be treated as unchanged from a prepopulated "Jane Doe").
 */
export function isUnchangedFromPrefilledValue(
  value: unknown,
  prefilledValue: unknown,
): boolean {
  if (typeof value === "string" && typeof prefilledValue === "string") {
    return value.trim().toLowerCase() === prefilledValue.trim().toLowerCase();
  }
  return value === prefilledValue;
}

export function arsErsUserHasFilledNecessaryFields({
  formLastUpdatedId,
  currentUserId,
  stableCurrentUserId,
  formData,
  draftData,
  fieldAuthors,
  isInRevisionsRequested,
}: {
  formLastUpdatedId: string | undefined;
  currentUserId: string | undefined;
  stableCurrentUserId?: string;
  formData: Record<string, unknown> | undefined;
  draftData: Record<string, unknown> | undefined;
  fieldAuthors: Record<string, string> | undefined;
  isInRevisionsRequested: boolean | undefined;
}): boolean {
  if (currentUserId === undefined && stableCurrentUserId === undefined) {
    return false;
  }
  const isCurrentUser = (id: string | undefined) =>
    id !== undefined && (id === currentUserId || id === stableCurrentUserId);

  const isFieldFilled = (
    field: string,
    data: Record<string, unknown> | undefined,
  ) => !!data?.[field];

  // Requires a real (non-empty) value, not just an author — a field a user
  // touched and left blank shouldn't count as having started a block.
  const isFieldByCurrentUser = (field: string) =>
    isCurrentUser(fieldAuthors?.[field]) && isFieldFilled(field, draftData);

  // A block is "started" if any user has written a real (non-empty) value to
  // any of its fields in draftData. A block whose only touched fields are
  // blank is treated as if it were never started, rather than as incomplete.
  // Every started block must be fully complete in formData before submission is allowed.
  const allStartedBlocksComplete = Object.values(
    US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS,
  ).every((formValues) => {
    const isStarted = formValues.some((field) =>
      isFieldFilled(field, draftData),
    );
    if (!isStarted) return true;
    return formValues.every((field) => isFieldFilled(field, formData));
  });

  // The current user must have personally authored at least one blocking field
  // with a real value. This prevents a user who only filled non-blocking fields
  // (e.g. remarks) — or only blanked out a blocking field — from submitting on
  // the strength of a different user's blocking-field edits.
  // Exception: while the opportunity is in "revisions requested" status, a role's
  // blocking fields are often already complete from a prior submission, so editing
  // that role's remarks field also counts.
  const atLeastOneBlockStarted = Object.entries(
    US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS,
  ).some(
    ([role, formValues]) =>
      formValues.some((field) => isFieldByCurrentUser(field)) ||
      (isInRevisionsRequested &&
        isFieldByCurrentUser(US_TX_ARS_ERS_REMARKS_FIELDS[role])),
  );

  return (
    atLeastOneBlockStarted &&
    allStartedBlocksComplete &&
    isCurrentUser(formLastUpdatedId)
  );
}

/**
 * Returns the key (from US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS) of the first
 * block the current user has started editing — i.e. authored a real
 * (non-empty) value for at least one of that block's blocking fields — or
 * undefined if the current user hasn't started any block.
 */
export function arsErsSectionEditedByCurrentUser({
  currentUserId,
  stableCurrentUserId,
  draftData,
  fieldAuthors,
}: {
  currentUserId: string | undefined;
  stableCurrentUserId?: string;
  draftData: Record<string, unknown> | undefined;
  fieldAuthors: Record<string, string> | undefined;
}): UsTxArsErsBlockingSubmitFieldKey | undefined {
  if (currentUserId === undefined && stableCurrentUserId === undefined) {
    return undefined;
  }

  const isFieldByCurrentUser = (field: string) => {
    const author = fieldAuthors?.[field];
    const isCurrentUser =
      author !== undefined &&
      (author === currentUserId || author === stableCurrentUserId);
    return isCurrentUser && !!draftData?.[field];
  };

  const keys = Object.keys(
    US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS,
  ) as UsTxArsErsBlockingSubmitFieldKey[];

  return keys.find((key) =>
    US_TX_ARS_ERS_BLOCKING_SUBMIT_FIELDS[key].some(isFieldByCurrentUser),
  );
}

const HEADERS_TO_HOIST = ["Current Fees", "Most Recent Payments"];

export function getDynamicCaseNoteHeaders(opportunity: Opportunity): string[] {
  const { record } = opportunity;
  if (!record) return [];

  const { caseNotes } = record;
  if (caseNotes && HEADERS_TO_HOIST.some((header) => header in caseNotes)) {
    return HEADERS_TO_HOIST;
  }
  return [];
}
